<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-w-lg"
      data-cy="save-snippet-dialog"
    >
      <DialogHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
        <DialogTitle class="text-lg font-semibold">{{ t('saveResponse') }}</DialogTitle>
      </DialogHeader>

      <div class="">
        <!-- Title Section -->
        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-gray-900">{{ t('title') }}</span>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium
                text-purple-700 hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isSaving || isGeneratingTitle || !content"
              data-cy="generate-title-button"
              @click="generateTitle"
            >
              <span
                v-if="isGeneratingTitle"
                class="animate-spin"
              >
                <LoaderCircle class="h-3 w-3" />
              </span>
              <span v-else>
                <RotateCw class="h-3 w-3" />
              </span>
              {{ t('generateWithAI') }}
            </button>
          </div>
          <InputField
            v-model="responseTitle"
            name="response-title"
            :placeholder="t('responseTitlePlaceholder')"
            :rules="yup.string().required().max(50)"
            data-cy="snippet-title-input"
          />
          <div class="text-right text-xs text-gray-500">
            {{ responseTitle.length }}/50 characters
          </div>
        </div>

        <!-- Content Section -->
        <div class="">
          <TextAreaField
            v-model="responseContent"
            name="response-content"
            :label="t('content')"
            rows="12"
            :disabled="true"
            :rules="yup.string().required()"
            data-cy="snippet-content-textarea"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex gap-2 pt-3">
        <button
          class="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          data-cy="snippet-cancel-button"
          @click="closeDialog"
        >
          {{ t('cancel') }}
        </button>
        <button
          :disabled="!isValid || isSaving"
          class="inline-flex flex-1 items-center justify-center rounded-md bg-purple-600 px-4 py-2 text-sm
            font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500
            focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-cy="snippet-save-button"
          @click="saveResponse"
        >
          <LoaderCircle
            v-if="isSaving"
            class="mr-2 h-4 w-4 animate-spin"
          />
          {{ t('save') }}
        </button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { SavedResponsesService } from '@/types/generated/services.gen'
  import { AIChatSessionTypeEnum } from '@/types/generated/types.gen'
  import { LoaderCircle, RotateCw } from 'lucide-vue-next'
  import { computed, inject, ref, watch } from 'vue'
  import * as yup from 'yup'

  const props = defineProps<{
    content: string
    caseUuid?: string
  }>()

  const emit = defineEmits(['close'])

  // Inject session type from parent AiChat component
  const sessionType = inject<keyof typeof AIChatSessionTypeEnum>(
    'sessionType',
    AIChatSessionTypeEnum.CASE
  )

  const { t } = useTranslation({
    en: {
      saveResponse: 'Save snippet',
      title: 'Title',
      responseTitlePlaceholder: 'Saved snippet title',
      content: 'Content',
      cancel: 'Cancel',
      save: 'Save',
      responseSaved: 'Response saved successfully',
      errorSaving: 'Error saving response',
      generateWithAI: 'Generate with AI',
      generatingTitle: 'Generating...',
      titleGenerationError: 'Error generating title'
    },
    de: {
      saveResponse: 'Antwort speichern',
      title: 'Titel',
      responseTitlePlaceholder: 'Gespeicherte Antwort Titel',
      content: 'Inhalt',
      cancel: 'Abbrechen',
      save: 'Speichern',
      responseSaved: 'Antwort erfolgreich gespeichert',
      errorSaving: 'Fehler beim Speichern der Antwort',
      generateWithAI: 'Mit KI generieren',
      generatingTitle: 'Generiere...',
      titleGenerationError: 'Fehler beim Generieren des Titels'
    }
  })

  const { toast } = useToast()

  // Initialize with true so it's open when mounted
  const isOpen = ref(true)
  const responseTitle = ref('')
  const responseContent = ref(props.content)
  const isSaving = ref(false)
  const isGeneratingTitle = ref(false)

  const isValid = computed(() => responseTitle.value.trim().length > 0)

  // Watch for dialog close event (when clicking outside)
  watch(isOpen, (newValue) => {
    if (!newValue) {
      emit('close')
    }
  })

  const closeDialog = () => {
    isOpen.value = false
  }

  const generateTitle = async () => {
    if (!props.content || isGeneratingTitle.value) return

    isGeneratingTitle.value = true
    responseTitle.value = ''

    try {
      const response = await SavedResponsesService.savedResponsesGenerateTitleCreate({
        requestBody: {
          content: props.content
        }
      })

      // Simulate streaming effect for better UX
      const title = response.title
      let currentIndex = 0

      const streamInterval = setInterval(() => {
        if (currentIndex < title.length) {
          responseTitle.value += title[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingTitle.value = false
        }
      }, 15)
    } catch (error) {
      console.error('Error generating title:', error)
      toast({
        title: t('titleGenerationError'),
        variant: 'destructive'
      })
      isGeneratingTitle.value = false
    }
  }

  const saveResponse = async () => {
    if (!isValid.value) return

    isSaving.value = true

    try {
      await SavedResponsesService.savedResponsesCreate({
        requestBody: {
          title: responseTitle.value.trim(),
          content: responseContent.value,
          session_type: sessionType
        }
      })

      toast({
        title: t('responseSaved'),
        variant: 'success'
      })

      // Dispatch a custom event to notify that a response was saved
      window.dispatchEvent(
        new CustomEvent('save-response-dialog-closed', { detail: { saved: true } })
      )

      closeDialog()
    } catch (error) {
      console.error('Error saving response:', error)
      toast({
        title: t('errorSaving'),
        variant: 'destructive'
      })
    } finally {
      isSaving.value = false
    }
  }
</script>
